/**
 * TanStack hook for the viewer's briefing (Story 24.10) — the ONLY briefing
 * client, so the Tasks badge, the agent tree and Today share one cache entry.
 *
 * Key `['briefing', tenantId, userId]` (both scope the cache: the waiting
 * count is per viewer). Disabled until both ids exist. Polled every 60s,
 * refetched on window focus and on every pathname change after the first
 * render (`cancelRefetch: false`, so a navigation joins an in-flight fetch
 * instead of restarting it). `staleTime: 0` with `refetchOnMount: false`: a
 * second reader mounting later reads the cache and never fetches on mount.
 * No retry.
 *
 * `waitingCount` is `undefined` whenever the query is in error or has no data
 * — a failed poll hides the badge rather than showing a stale number.
 *
 * Story 29.1 — `unreadCount` (conversations with unread replies) follows the
 * same rule, and is also `undefined` when the payload has no `unread` block
 * (an older harness, an e2e mock).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchBriefing, type Briefing } from '@/lib/api/briefing';
import { useAuth } from '@/lib/contexts/AuthContext';

/** Key prefix — invalidate `['briefing']` to refetch every viewer's entry. */
export const BRIEFING_QUERY_KEY = ['briefing'] as const;

export function useBriefing() {
  const { user, tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  const userId = user?.id ?? null;
  const enabled = tenantId !== null && userId !== null;

  const query = useQuery<Briefing, Error>({
    queryKey: [...BRIEFING_QUERY_KEY, tenantId, userId],
    queryFn: ({ signal }) => fetchBriefing(signal),
    enabled,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    retry: false,
  });

  const { pathname } = useLocation();
  const lastPathname = useRef(pathname);
  const { refetch } = query;
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    // `refetch` ignores `enabled`, so the gate is repeated here.
    if (enabled) void refetch({ cancelRefetch: false });
  }, [pathname, enabled, refetch]);

  const waitingCount = query.isError || !query.data ? undefined : query.data.waiting.total;
  const unreadCount = query.isError || !query.data ? undefined : query.data.unread?.total;
  return { ...query, waitingCount, unreadCount };
}
